import { CALIBRATION_MAX, CALIBRATION_MIN, SENSOR_LOCATION_MAX, SENSOR_NAME_MAX } from '@jums-dashboard/shared';
import { useEffect, useId, useState } from 'react';

import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';

import useCreateSensorFromUnknown from '../hooks/useCreateSensorFromUnknown';
import { formatTimestamp } from '../lib/format';
import type { UnknownSensor } from '../types/unknownSensor';
import ReadOnlyRow from './ReadOnlyRow';

interface Props {
  open: boolean;
  homeId: string;
  unknown: UnknownSensor | null;
  onClose: () => void;
  onCreated?: () => void;
}

export default function NewSensorModal({ open, homeId, unknown, onClose, onCreated }: Props) {
  const create = useCreateSensorFromUnknown();
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [offsetText, setOffsetText] = useState('0');

  const nameId = useId();
  const locationId = useId();
  const offsetId = useId();

  useEffect(() => {
    if (!open) {
      setName('');
      setLocation('');
      setOffsetText('0');
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!unknown) return null;

  const trimmedName = name.trim();
  const trimmedLocation = location.trim();
  const parsedOffset = Number(offsetText);
  const offsetValid = Number.isFinite(parsedOffset) && parsedOffset >= CALIBRATION_MIN && parsedOffset <= CALIBRATION_MAX;
  const nameValid = trimmedName.length >= 1 && trimmedName.length <= SENSOR_NAME_MAX;
  const locationValid = trimmedLocation.length <= SENSOR_LOCATION_MAX;
  const canSubmit = nameValid && locationValid && offsetValid && !create.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      manageHistory={false}
      title="Create sensor"
      description="Promote this unknown sensor into a configured sensor."
      size="md"
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          create.mutate(
            {
              homeId,
              physicalId: unknown.physicalId,
              name: trimmedName,
              location: trimmedLocation,
              calibrationOffsetC: parsedOffset,
            },
            {
              onSuccess: () => {
                onCreated?.();
                onClose();
              },
            },
          );
        }}
      >
        <div className="rounded-lg border border-(--border-subtle) bg-(--bg-surface) px-3 py-2">
          <ReadOnlyRow label="Physical id" value={unknown.physicalId} mono copyLabel="physical id" />
          <ReadOnlyRow label="Source" value={unknown.source} />
          <ReadOnlyRow label="Last seen" value={formatTimestamp(unknown.lastSeenAt)} />
        </div>

        <div className="space-y-1.5">
          <label htmlFor={nameId} className="text-xs font-medium text-(--text-secondary)">
            Name
          </label>
          <input
            id={nameId}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={SENSOR_NAME_MAX}
            placeholder="Sauna upper bench"
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor={locationId} className="text-xs font-medium text-(--text-secondary)">
            Location <span className="text-(--text-tertiary)">(optional)</span>
          </label>
          <input
            id={locationId}
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={SENSOR_LOCATION_MAX}
            placeholder="Sauna"
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor={offsetId} className="text-xs font-medium text-(--text-secondary)">
            Calibration offset (°C)
          </label>
          <input
            id={offsetId}
            type="number"
            step="0.1"
            min={CALIBRATION_MIN}
            max={CALIBRATION_MAX}
            value={offsetText}
            onChange={(e) => setOffsetText(e.target.value)}
            className="w-full rounded-md border border-(--border-default) bg-(--bg-surface) px-3 py-2 text-sm text-(--text-primary) outline-none focus:border-(--accent-primary) focus:ring-2 focus:ring-(--accent-primary)/40"
          />
          <p className="text-xs text-(--text-tertiary)">
            Added to every raw temperature reading. Range {CALIBRATION_MIN}…{CALIBRATION_MAX}°C.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={create.isPending} disabled={!canSubmit}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
